import React, {useRef} from 'react';

import {Button, Icon, Text, WithTooltip} from '@albert-io/atomic';
import {
  ANNOTATION_MODE_ON_TOOLTIP,
  NOTE_TOOLTIP_PROPS
} from 'client/components/PracticeView/Annotations/annotationConstants';
import {
  AnnotationModeCoachmark,
  useAnnotationModeCoachmark
} from 'client/components/PracticeView/Annotations/AnnotationModeCoachmark';
import {useAnnotationModeStore} from 'client/components/PracticeView/Annotations/AnnotationModeStore';
import {ANNOTATION_FOCUS_FALLBACK_PROPS} from 'client/components/PracticeView/Annotations/annotationFocusFallback';
import {useIsSessionOnlyNoticeOpen} from 'client/components/PracticeView/Annotations/SessionOnlyAnnotationNotice';
import {useAnnotationModeEscape} from 'client/components/PracticeView/Annotations/useAnnotationModeEscape';
import {useIsSubjectPracticeAnnotationAvailable} from 'client/SubjectPracticeView/useIsSubjectPracticeAnnotationAvailable';

interface SubjectAnnotationModeToggleProps {
  /** The guide level practiced, or the page without one; keys the coachmark's one showing. */
  practiceId: string;
}

/** Labelled rather than icon-only: every other control in this toolbar is a text button. */
export default function SubjectAnnotationModeToggle({
  practiceId
}: SubjectAnnotationModeToggleProps) {
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const isAvailable = useIsSubjectPracticeAnnotationAvailable();
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const toggleAnnotationMode = useAnnotationModeStore((state) => state.toggleAnnotationMode);
  const isSessionOnlyNoticeOpen = useIsSessionOnlyNoticeOpen();
  const coachmark = useAnnotationModeCoachmark({
    scopeKey: `subject-practice:${practiceId}`,
    isHeldBack: isSessionOnlyNoticeOpen
  });
  useAnnotationModeEscape(toggleButtonRef);

  if (!isAvailable) {
    return null;
  }

  const handleClick = () => {
    coachmark.offer(toggleButtonRef.current);
    toggleAnnotationMode();
  };

  return (
    <>
      <WithTooltip
        {...NOTE_TOOLTIP_PROPS}
        content={isAnnotationModeOn ? ANNOTATION_MODE_ON_TOOLTIP : 'Turn on annotation mode'}
        enabled={coachmark.target === null}
        placement='bottom'
      >
        <Button
          ref={toggleButtonRef}
          {...ANNOTATION_FOCUS_FALLBACK_PROPS}
          aria-pressed={isAnnotationModeOn}
          color={isAnnotationModeOn ? 'primary' : 'secondary'}
          data-testid='subject-practice-view__annotation-mode-toggle'
          onClick={handleClick}
          roundness='pill'
          size='s'
          variant={isAnnotationModeOn ? 'solid' : 'text'}
        >
          <Icon icon='highlighter' iconStyle='regular' className='mr-x1' />
          <Text bold size='s' color='inherit'>
            Highlight &amp; Note
          </Text>
        </Button>
      </WithTooltip>
      {coachmark.target && (
        <AnnotationModeCoachmark target={coachmark.target} onDismiss={coachmark.dismiss} />
      )}
    </>
  );
}
